/**
 * MERGE ITEMS MODAL - ENHANCED VERSION
 * 
 * Allows users to merge 2+ duplicate items into one.
 * Features:
 * - Select primary item (keeps name, brand)
 * - EDITABLE quantity (not just auto-sum)
 * - Photo picker - choose default photo from all items
 * - Field conflict resolver - pick which values to keep
 * - Live preview of merged result
 * - Tags merge options
 * - Returns merged item to parent (no full page reload needed)
 * - Deletes duplicate items after merge
 */

import React, { useState, useMemo, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { CollectionItem } from '../types';
import {
  X, Merge, Loader2, Check, ChevronDown, ChevronUp, Image, Hash,
  Star, Sparkles, Package, Tag, MessageSquare, Palette
} from 'lucide-react';

interface MergeItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: CollectionItem[];
  userId: string;
  onMergeComplete?: (mergedItem: CollectionItem, deletedIds: string[]) => void;
}

// Helper type for field conflicts
interface FieldConflict {
  field: string;
  label: string;
  icon: React.ReactNode;
  values: { itemId: string; value: string; itemName: string }[];
}

const MergeItemsModal: React.FC<MergeItemsModalProps> = ({
  isOpen,
  onClose,
  items,
  userId,
  onMergeComplete
}) => {
  const [primaryItemId, setPrimaryItemId] = useState<string>(items[0]?.id || '');
  const [merging, setMerging] = useState(false);
  
  // NEW: Editable quantity
  const [customQuantity, setCustomQuantity] = useState<number | null>(null);
  
  // NEW: Photo selection
  const [selectedDefaultPhotoUrl, setSelectedDefaultPhotoUrl] = useState<string | null>(null);
  const [showPhotoSection, setShowPhotoSection] = useState(true);
  
  // NEW: Field conflict resolutions
  const [fieldSelections, setFieldSelections] = useState<Record<string, string>>({});
  const [showFieldsSection, setShowFieldsSection] = useState(true);
  
  // NEW: Notes handling
  const [notesMergeMode, setNotesMergeMode] = useState<'combine' | 'pick'>('combine');
  const [selectedNotesItemId, setSelectedNotesItemId] = useState<string | null>(null);
  
  // NEW: Tags handling
  const [tagsMergeMode, setTagsMergeMode] = useState<'combine' | 'primary'>('combine');

  // Reset state when items change
  useEffect(() => {
    if (items.length > 0) {
      setPrimaryItemId(items[0].id);
      setCustomQuantity(null);
      setSelectedDefaultPhotoUrl(null);
      setFieldSelections({});
      setSelectedNotesItemId(null);
    }
  }, [items]);

  // Calculate auto-sum quantity
  const autoSumQuantity = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }, [items]);

  // Collect all unique photos from all items
  const allPhotos = useMemo(() => {
    const photos: { url: string; caption?: string; isDefault: boolean; fromItemId: string; fromItemName: string }[] = [];
    const seenUrls = new Set<string>();

    items.forEach(item => {
      item.photos?.forEach(photo => {
        if (!seenUrls.has(photo.url)) {
          seenUrls.add(photo.url);
          photos.push({
            ...photo,
            fromItemId: item.id,
            fromItemName: item.name
          });
        }
      });
    });

    return photos;
  }, [items]);

  // Find field conflicts (different non-empty values across items)
  const fieldConflicts = useMemo((): FieldConflict[] => {
    const conflicts: FieldConflict[] = [];
    
    const fieldsToCheck: { field: keyof CollectionItem; label: string; icon: React.ReactNode }[] = [
      { field: 'colorDescription', label: 'Color Description', icon: <Palette className="icon-sm" /> },
      { field: 'collectionName', label: 'Collection', icon: <Sparkles className="icon-sm" /> },
      { field: 'eventSource', label: 'Event/Source', icon: <Package className="icon-sm" /> },
      { field: 'storage', label: 'Storage Location', icon: <Package className="icon-sm" /> },
    ];

    fieldsToCheck.forEach(({ field, label, icon }) => {
      const uniqueValues: { itemId: string; value: string; itemName: string }[] = [];
      const seenValues = new Set<string>();

      items.forEach(item => {
        const value = item[field];
        if (value && typeof value === 'string' && value.trim() !== '' && !seenValues.has(value.toLowerCase())) {
          seenValues.add(value.toLowerCase());
          uniqueValues.push({ itemId: item.id, value: value as string, itemName: item.name });
        }
      });

      if (uniqueValues.length > 1) {
        conflicts.push({ field, label, icon, values: uniqueValues });
      }
    });

    return conflicts;
  }, [items]);

  // Items with notes
  const itemsWithNotes = useMemo(() => {
    return items.filter(item => item.notes && item.notes.trim() !== '');
  }, [items]);

  // Calculate merged result preview
  const mergedPreview = useMemo(() => {
    if (items.length < 2) return null;

    const primaryItem = items.find(item => item.id === primaryItemId) || items[0];
    const otherItems = items.filter(item => item.id !== primaryItemId);

    // Build photos array with selected default
    const mergedPhotos = allPhotos.map(photo => ({
      url: photo.url,
      caption: photo.caption,
      isDefault: selectedDefaultPhotoUrl 
        ? photo.url === selectedDefaultPhotoUrl 
        : photo.url === allPhotos[0]?.url
    }));

    // Use custom quantity or auto-sum
    const finalQuantity = customQuantity !== null ? customQuantity : autoSumQuantity;

    // Sum timesUsed
    const totalTimesUsed = items.reduce((sum, item) => sum + (item.timesUsed || 0), 0);

    // Handle notes based on mode
    let finalNotes = '';
    if (notesMergeMode === 'combine') {
      const allNotes = items
        .map(item => item.notes?.trim())
        .filter(Boolean);
      finalNotes = allNotes.length > 1 
        ? allNotes.join('\n---\n') 
        : allNotes[0] || '';
    } else if (selectedNotesItemId) {
      const selectedItem = items.find(i => i.id === selectedNotesItemId);
      finalNotes = selectedItem?.notes || '';
    }

    // OR boolean flags (if any item has it, result has it)
    const isFavorite = items.some(item => item.isFavorite);
    const isRareLE = items.some(item => item.isRareLE);
    const needsInfo = items.some(item => item.needsInfo);
    const onWishlist = items.some(item => item.onWishlist);

    // Resolve field conflicts
    const getFieldValue = (field: string): string => {
      // If user made a selection, use it
      if (fieldSelections[field]) {
        const selectedItem = items.find(i => i.id === fieldSelections[field]);
        if (selectedItem) return (selectedItem as any)[field] || '';
      }
      // Otherwise use primary item's value or first non-empty
      if ((primaryItem as any)[field]) return (primaryItem as any)[field];
      for (const item of otherItems) {
        if ((item as any)[field]) return (item as any)[field];
      }
      return '';
    };

    // Handle tags based on mode
    let finalTags: string[] = [];
    if (tagsMergeMode === 'combine') {
      const allTags = new Set<string>();
      items.forEach(item => {
        item.tags?.forEach(tag => allTags.add(tag));
      });
      finalTags = Array.from(allTags);
    } else {
      finalTags = primaryItem.tags || [];
    }

    // Combine array fields (finish, particles, etc.) - merge all unique values
    const combineArrayField = (field: keyof CollectionItem): string[] => {
      const combined = new Set<string>();
      items.forEach(item => {
        const value = item[field];
        if (Array.isArray(value)) {
          value.forEach(v => {
            if (typeof v === 'string') combined.add(v);
          });
        }
      });
      return Array.from(combined);
    };

    return {
      primaryItem,
      itemsToDelete: otherItems,
      merged: {
        // Keep primary's identity fields
        id: primaryItem.id,
        name: primaryItem.name,
        brand: primaryItem.brand,
        itemType: primaryItem.itemType,
        category: primaryItem.category,
        
        // Merged data
        photos: mergedPhotos,
        quantity: finalQuantity,
        timesUsed: totalTimesUsed,
        notes: finalNotes,
        tags: finalTags,
        
        // Boolean flags (OR'd)
        isFavorite,
        isRareLE,
        needsInfo,
        onWishlist,
        
        // Resolved conflict fields
        colorDescription: getFieldValue('colorDescription'),
        collectionName: getFieldValue('collectionName'),
        // Read from canonical eventName, fall back to legacy eventSource
        eventName: getFieldValue('eventName') || getFieldValue('eventSource'),
        eventSource: null, // Clear legacy field (canonical is eventName)
        storage: getFieldValue('storage'),
        
        // Combined array fields
        finish: combineArrayField('finish'),
        particles: combineArrayField('particles'),
        holoType: combineArrayField('holoType'),
        specialFeatures: combineArrayField('specialFeatures'),
        colorFamily: combineArrayField('colorFamily'),

        // Keep primary's other fields
        baseType: primaryItem.baseType,
        colorShift: primaryItem.colorShift,
        isForDestash: primaryItem.isForDestash,
        isDestashed: primaryItem.isDestashed,
        destashStatus: primaryItem.destashStatus,
        destashPrice: primaryItem.destashPrice,
        isOpenToSwaps: primaryItem.isOpenToSwaps,
        isoPriority: primaryItem.isoPriority,
        primaryColor: primaryItem.primaryColor,
        releaseType: primaryItem.releaseType,
        exclusiveRetailer: primaryItem.exclusiveRetailer,
        releaseYear: primaryItem.releaseYear,
        releaseSeason: primaryItem.releaseSeason,
      },
      stats: {
        totalPhotos: mergedPhotos.length,
        totalQuantity: finalQuantity,
        totalTimesUsed,
        itemsBeingMerged: items.length,
        hasConflicts: fieldConflicts.length > 0,
      }
    };
  }, [items, primaryItemId, allPhotos, selectedDefaultPhotoUrl, customQuantity, autoSumQuantity, notesMergeMode, selectedNotesItemId, fieldSelections, tagsMergeMode, fieldConflicts.length]);

  if (!isOpen || items.length < 2) return null;

  // Get display image for an item
  const getDisplayImage = (item: CollectionItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    return null;
  };

  // Remove undefined values from an object (Firestore doesn't accept undefined)
  const cleanUndefined = (obj: Record<string, any>): Record<string, any> => {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    return cleaned;
  };

  // Perform the merge
  const handleMerge = async () => {
    if (!mergedPreview || merging) return;

    setMerging(true);

    try {
      // 1. Update the primary item with merged data (remove undefined values)
      const primaryRef = doc(db, 'users', userId, 'items', primaryItemId);
      const cleanedData = cleanUndefined(mergedPreview.merged);
      
      // Remove id from the update data (can't update document ID)
      const { id, ...updateData } = cleanedData;
      
      await updateDoc(primaryRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      // 2. Delete the other items
      const deletedIds: string[] = [];
      for (const item of mergedPreview.itemsToDelete) {
        const itemRef = doc(db, 'users', userId, 'items', item.id);
        await deleteDoc(itemRef);
        deletedIds.push(item.id);
      }

      // 3. Call completion handler with merged item data (so parent can update local state)
      onMergeComplete?.(mergedPreview.merged as unknown as CollectionItem, deletedIds);
      onClose();
    } catch (err) {
      console.error('Error merging items:', err);
      alert('Failed to merge items. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="modal-light max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border border-gray-300 bg-gradient-to-r from-fuchsia-500 to-purple-500">
          <div className="flex items-center gap-2">
            <Merge className="icon-md text-white" />
            <h2 className="font-bold text-lg text-white font-body">
              Merge {items.length} Items
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition-default"
          >
            <X className="icon-md text-white" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          
          {/* Items Being Merged */}
          <div>
            <h3 className="text-sm font-bold text-light-secondary mb-2 flex items-center gap-2 font-body">
              <Merge className="icon-sm" />
              Items to Merge:
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {items.map(item => {
                const isPrimary = item.id === primaryItemId;
                return (
                  <div 
                    key={item.id}
                    onClick={() => setPrimaryItemId(item.id)}
                    className={`flex-shrink-0 flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all ${
                      isPrimary
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {getDisplayImage(item) ? (
                      <img src={getDisplayImage(item)!} alt="" className="w-12 h-12 rounded object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded rainbow-gradient flex items-center justify-center text-xl">
                        💅
                      </div>
                    )}
                    <div className="max-w-[120px]">
                      <p className="text-sm font-bold text-light-primary truncate font-body">
                        {item.name}
                      </p>
                      <p className="text-xs text-light-muted font-body">
                        Qty: {item.quantity || 1}
                      </p>
                    </div>
                    {isPrimary && (
                      <span className="px-2 py-0.5 bg-gray-700 text-white text-xs font-bold rounded-full whitespace-nowrap">
                        Primary
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-light-muted mt-2 font-body">
              Click an item to make it the primary (keeps name & brand)
            </p>
          </div>

          {/* Quantity Control */}
          <div className="border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-light-secondary mb-3 flex items-center gap-2 font-body">
              <Hash className="icon-sm" />
              Final Quantity
            </h3>
            
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCustomQuantity(null)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-default font-body ${
                    customQuantity === null
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-light-secondary hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Sum All ({autoSumQuantity})
                </button>
                <button
                  onClick={() => setCustomQuantity(1)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                    customQuantity === 1
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-light-secondary hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Keep as 1
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-light-muted">or</span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={customQuantity !== null ? customQuantity : ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val > 0) {
                      setCustomQuantity(val);
                    } else if (e.target.value === '') {
                      setCustomQuantity(null);
                    }
                  }}
                  placeholder="Custom"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center font-bold font-body"
                />
              </div>
            </div>
            
            <p className="text-xs text-light-muted mt-2 font-body">
              Individual quantities: {items.map(i => i.quantity || 1).join(' + ')} = {autoSumQuantity}
            </p>
          </div>

          {/* Photo Selection */}
          {allPhotos.length > 0 && (
            <div className="border border-gray-200 rounded-xl p-4">
              <button 
                onClick={() => setShowPhotoSection(!showPhotoSection)}
                className="w-full flex items-center justify-between text-sm font-bold text-light-secondary mb-3 font-body"
              >
                <span className="flex items-center gap-2">
                  <Image className="icon-sm" />
                  Choose Default Photo ({allPhotos.length} total)
                </span>
                {showPhotoSection ? <ChevronUp className="icon-sm" /> : <ChevronDown className="icon-sm" />}
              </button>
              
              {showPhotoSection && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {allPhotos.map((photo, idx) => {
                    const isSelected = selectedDefaultPhotoUrl 
                      ? photo.url === selectedDefaultPhotoUrl 
                      : idx === 0;
                    return (
                      <button
                        key={photo.url}
                        onClick={() => setSelectedDefaultPhotoUrl(photo.url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          isSelected 
                            ? 'border border-gray-300 ring-2 ring-slate-500' 
                            : 'border-transparent hover:border border-gray-300'
                        }`}
                      >
                        <img src={photo.url} alt="" className="w-full h-full object-cover" />
                        {isSelected && (
                          <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                            <div className="bg-purple-600 rounded-full p-1">
                              <Check className="icon-sm text-white" />
                            </div>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5">
                          <p className="text-[10px] text-white truncate">{photo.fromItemName}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Field Conflicts */}
          {fieldConflicts.length > 0 && (
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <button 
                onClick={() => setShowFieldsSection(!showFieldsSection)}
                className="w-full flex items-center justify-between text-sm font-bold text-amber-700 mb-3 font-body"
              >
                <span className="flex items-center gap-2">
                  ⚠️ Resolve Conflicts ({fieldConflicts.length})
                </span>
                {showFieldsSection ? <ChevronUp className="icon-sm" /> : <ChevronDown className="icon-sm" />}
              </button>
              
              {showFieldsSection && (
                <div className="space-y-4">
                  {fieldConflicts.map(conflict => (
                    <div key={conflict.field} className="bg-white rounded-lg p-3">
                      <p className="text-sm font-bold text-light-secondary mb-2 flex items-center gap-2 font-body">
                        {conflict.icon}
                        {conflict.label}
                      </p>
                      <div className="space-y-1">
                        {conflict.values.map(({ itemId, value, itemName }) => (
                          <label 
                            key={itemId}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-default ${
                              fieldSelections[conflict.field] === itemId || (!fieldSelections[conflict.field] && itemId === primaryItemId)
                                ? 'bg-purple-50 border border-purple-300'
                                : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`conflict-${conflict.field}`}
                              checked={fieldSelections[conflict.field] === itemId || (!fieldSelections[conflict.field] && itemId === primaryItemId)}
                              onChange={() => setFieldSelections(prev => ({ ...prev, [conflict.field]: itemId }))}
                              className="text-light-primary focus:ring-slate-500"
                            />
                            <span className="text-sm text-light-secondary flex-1 font-body">
                              {value}
                            </span>
                            <span className="text-xs text-light-muted">from {itemName}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes Handling */}
          {itemsWithNotes.length > 1 && (
            <div className="border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-bold text-light-secondary mb-3 flex items-center gap-2 font-body">
                <MessageSquare className="icon-sm" />
                Notes ({itemsWithNotes.length} items have notes)
              </h3>
              
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setNotesMergeMode('combine')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                    notesMergeMode === 'combine'
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-light-secondary hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Combine All
                </button>
                <button
                  onClick={() => {
                    setNotesMergeMode('pick');
                    setSelectedNotesItemId(itemsWithNotes[0]?.id || null);
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                    notesMergeMode === 'pick'
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-light-secondary hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Pick One
                </button>
              </div>
              
              {notesMergeMode === 'pick' && (
                <div className="space-y-2">
                  {itemsWithNotes.map(item => (
                    <label 
                      key={item.id}
                      className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-default ${
                        selectedNotesItemId === item.id
                          ? 'bg-purple-50 border border-purple-300'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="radio"
                        name="notes-pick"
                        checked={selectedNotesItemId === item.id}
                        onChange={() => setSelectedNotesItemId(item.id)}
                        className="mt-1 text-light-primary focus:ring-slate-500"
                      />
                      <div className="flex-1">
                        <p className="text-xs text-light-muted mb-1">{item.name}</p>
                        <p className="text-sm text-light-secondary line-clamp-2 font-body">
                          {item.notes}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tags Handling */}
          {items.some(i => i.tags && i.tags.length > 0) && (
            <div className="border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-bold text-light-secondary mb-3 flex items-center gap-2 font-body">
                <Tag className="icon-sm" />
                Tags
              </h3>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setTagsMergeMode('combine')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                    tagsMergeMode === 'combine'
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-light-secondary hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Combine All Tags
                </button>
                <button
                  onClick={() => setTagsMergeMode('primary')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                    tagsMergeMode === 'primary'
                      ? 'bg-gray-700 text-white'
                      : 'bg-white text-light-secondary hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Primary Only
                </button>
              </div>
            </div>
          )}

          {/* Live Preview */}
          {mergedPreview && (
            <div className="bg-gradient-to-br from-fuchsia-50 to-purple-50 rounded-xl p-4 border border-gray-300">
              <h3 className="text-sm font-bold text-light-primary mb-3 flex items-center gap-2 font-body">
                <Star className="icon-sm" />
                Merged Result Preview
              </h3>
              
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex gap-4">
                  {/* Preview Image */}
                  <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    {mergedPreview.merged.photos && mergedPreview.merged.photos.length > 0 ? (
                      <img 
                        src={mergedPreview.merged.photos.find(p => p.isDefault)?.url || mergedPreview.merged.photos[0].url} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rainbow-gradient flex items-center justify-center text-3xl">
                        💅
                      </div>
                    )}
                  </div>
                  
                  {/* Preview Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-light-primary font-bold font-heading">
                      {mergedPreview.merged.brand}
                    </p>
                    <p className="font-bold text-light-primary truncate font-body">
                      {mergedPreview.merged.name}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-2 py-1 bg-gray-100 text-light-primary rounded-full text-xs font-bold">
                        Qty: {mergedPreview.stats.totalQuantity}
                      </span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                        {mergedPreview.stats.totalPhotos} photos
                      </span>
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold">
                        Used {mergedPreview.stats.totalTimesUsed}x
                      </span>
                    </div>
                    
                    {/* Show resolved values */}
                    {mergedPreview.merged.colorDescription && (
                      <p className="text-xs text-light-muted mt-2 truncate">
                        {mergedPreview.merged.colorDescription}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Boolean flags */}
                <div className="flex gap-2 mt-3 flex-wrap">
                  {mergedPreview.merged.isFavorite && (
                    <span className="px-2 py-1 bg-pink-100 text-pink-600 rounded-full text-xs">❤️ Favorite</span>
                  )}
                  {mergedPreview.merged.isRareLE && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs">💎 Rare/LE</span>
                  )}
                  {mergedPreview.merged.onWishlist && (
                    <span className="px-2 py-1 bg-sky-100 text-sky-600 rounded-full text-xs">⭐ ISO</span>
                  )}
                </div>
              </div>

              {/* Warning */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-body">
                  ⚠️ <strong>{mergedPreview.itemsToDelete.length} item{mergedPreview.itemsToDelete.length !== 1 ? 's' : ''}</strong> will be deleted after merge. This cannot be undone.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border border-gray-300 border border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-light-secondary hover:bg-gray-200 rounded-lg transition-default font-medium font-body"
            disabled={merging}
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={merging}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-bold transition-default ${
              !merging
                ? 'bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white hover:from-fuchsia-600 hover:to-purple-600 shadow-lg'
                : 'bg-gray-300 text-light-muted cursor-not-allowed'
            }`}
          >
            {merging ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Merging...
              </>
            ) : (
              <>
                <Merge className="icon-sm" />
                Merge Items
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeItemsModal;
